import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { Activity, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "glpflo_desktop_alerts";

/** Tiny always-visible system health indicator for admin/IT sidebars.
 * Polls /admin/health every 30s. Shows a desktop notification on Healthy→Degraded transitions. */
export default function StatusBadge({ onClick, variant = "sidebar" }) {
  const [status, setStatus] = useState(null);
  const [workerAge, setWorkerAge] = useState(null);
  const [alertsEnabled, setAlertsEnabled] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
  const prevStatus = useRef(null);

  // Try to fire a desktop notification on Healthy → Degraded
  const notifyTransition = (toStatus) => {
    if (!alertsEnabled || typeof Notification === "undefined" || Notification.permission !== "granted") return;
    if (toStatus === "degraded") {
      try {
        new Notification("GLPFlo: System Degraded", {
          body: "The cron worker heartbeat is stale or the health endpoint is unreachable. Click to investigate.",
          icon: "/favicon.ico",
          tag: "glpflo-health",
          requireInteraction: true,
        });
      } catch { void 0; }
    } else if (toStatus === "healthy") {
      try {
        new Notification("GLPFlo: Recovered", {
          body: "Systems are operational again.",
          icon: "/favicon.ico",
          tag: "glpflo-health",
        });
      } catch { void 0; }
    }
  };

  const requestPerms = async () => {
    if (typeof Notification === "undefined") {
      toast.error("This browser doesn't support desktop notifications");
      return;
    }
    if (Notification.permission === "denied") {
      toast.error("Blocked — enable notifications for this site in your browser settings");
      return;
    }
    const p = await Notification.requestPermission();
    if (p === "granted") {
      localStorage.setItem(STORAGE_KEY, "1");
      setAlertsEnabled(true);
      toast.success("Desktop alerts enabled");
      try { new Notification("GLPFlo alerts enabled", { body: "You'll be notified if the system degrades.", icon: "/favicon.ico" }); } catch { void 0; }
    } else {
      toast.info("Permission denied");
    }
  };

  const disableAlerts = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAlertsEnabled(false);
    toast.info("Desktop alerts disabled");
  };

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await API.get("/admin/health");
        if (cancelled) return;
        setStatus(data.overall);
        setWorkerAge(data.worker?.age_seconds);
        if (prevStatus.current && prevStatus.current !== data.overall) {
          notifyTransition(data.overall);
        }
        prevStatus.current = data.overall;
      } catch {
        if (!cancelled) {
          if (prevStatus.current === "healthy") notifyTransition("degraded");
          prevStatus.current = "degraded";
          setStatus("degraded");
        }
      }
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertsEnabled]);

  if (!status) {
    return (
      <div className="inline-flex items-center gap-1.5 text-xs text-slate-400" data-testid="status-badge-loading">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-pulse"></span>
      </div>
    );
  }

  const healthy = status === "healthy";
  const title = healthy
    ? `All systems operational · worker heartbeat ${workerAge ?? "?"}s ago`
    : `Degraded — click to investigate (worker ${workerAge ?? "?"}s old)`;

  const textColor = variant === "sidebar" ? "text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white" : "text-slate-500 hover:text-slate-900";
  const bellMuted = variant === "sidebar" ? "text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" : "text-slate-400 hover:text-slate-600";

  const permission = typeof Notification !== "undefined" ? Notification.permission : "unsupported";

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        data-testid="status-badge"
        className={`inline-flex items-center gap-1.5 text-xs ${textColor} transition-colors`}
      >
        <span className="relative flex h-2 w-2" data-testid={`status-dot-${status}`}>
          {!healthy && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>}
          <span className={`relative inline-flex rounded-full h-2 w-2 ${healthy ? "bg-emerald-500" : "bg-rose-500"}`}></span>
        </span>
        <span className="hidden sm:inline">{healthy ? "Healthy" : "Degraded"}</span>
        <Activity className="w-3 h-3 opacity-50" />
      </button>
      {alertsEnabled && permission === "granted" ? (
        <button type="button" onClick={disableAlerts} title="Desktop alerts enabled — click to disable"
          aria-label="Disable desktop alerts" data-testid="alerts-on-btn"
          className={`inline-flex items-center ${bellMuted} transition-colors`}>
          <Bell className="w-3 h-3" />
        </button>
      ) : (
        <button type="button" onClick={requestPerms} title="Enable desktop alerts for status changes"
          aria-label="Enable desktop alerts" data-testid="alerts-off-btn"
          className={`inline-flex items-center ${bellMuted} transition-colors`}>
          <BellOff className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
